import React, { FC, useEffect, useState } from 'react';
import { Fit, Layout, useRive } from 'rive-react';
import { Box } from '@wix/design-system';
import file from '../../../../assets/SplashText.png';
import { useScreenWidth } from '../../../../hooks/useScreenWidth';
import { useUouLanguage } from '../../../../hooks/useUouLanguage';

const MOBILE_VIDEO_URL = 'https://video.wixstatic.com/video/8174c4_ee22d040925a4ca78bbe4d33edc67f41/1080p/mp4/file.mp4';
const DESKTOP_VIDEO_URL = 'https://video.wixstatic.com/video/8174c4_53d0281678754b5c9f0bc48c3319a260/720p/mp4/file.mp4';

enum LayerNames {
  Frame1Row1 = 'Frame1Line1',
  Frame1Row2 = 'Frame1Line2',
  Frame2Row1 = 'Frame2Line1',
  Frame2Row2 = 'Frame2Line2',
  Frame2Row3 = 'Frame2Line3Bold',
}

type RiveSplashScreenProps = {
  assistantName: string;
  businessName: string;
  onEnd: () => void;
};

export const RiveSplashScreen: FC<RiveSplashScreenProps> = ({ assistantName, businessName, onEnd }) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const { isMobile } = useScreenWidth();
  const language = useUouLanguage();
  const { rive, RiveComponent } = useRive({
    src: file,
    layout: new Layout({
      fit: Fit.Cover,
    }),
    onLoadError: onEnd,
  });

  const onVideoLoadedHandler = () => {
    setVideoLoaded(true);
  };

  // ✅ Set text dynamically inside Rive when it's ready
  useEffect(() => {
    if (rive && videoLoaded) {
      rive.setTextRunValue(LayerNames.Frame1Row1, language.ASA_STANDALONE_CHAT_SPLASH_FRAME1_LINE1(assistantName));
      rive.setTextRunValue(LayerNames.Frame1Row2, language.ASA_STANDALONE_CHAT_SPLASH_FRAME1_LINE2());
      rive.setTextRunValue(LayerNames.Frame2Row1, language.ASA_STANDALONE_CHAT_SPLASH_FRAME2_LINE1());
      rive.setTextRunValue(
        LayerNames.Frame2Row2,
        businessName ? language.ASA_STANDALONE_CHAT_SPLASH_FRAME2_LINE2() : language.ASA_STANDALONE_CHAT_SPLASH_FRAME2_LINE2_NO_BUSINESS_NAME(),
      );
      rive.setTextRunValue(LayerNames.Frame2Row3, language.ASA_STANDALONE_CHAT_SPLASH_FRAME2_LINE3(businessName));
      rive.play();
    } else {
      console.warn('⚠️ Rive or Video not available yet.');
    }
  }, [assistantName, businessName, language, rive, videoLoaded]); // Runs when Rive is initialized

  return (
    <>
      <Box width="100vw" height="100vh" position="absolute" top={0} left={0} zIndex={-2}>
        <video
          width="100%"
          height="100%"
          style={{ objectFit: 'cover' }}
          autoPlay
          muted
          playsInline
          preload="auto"
          onPlay={onVideoLoadedHandler}
          onEnded={onEnd}
        >
          <source src={isMobile ? MOBILE_VIDEO_URL : DESKTOP_VIDEO_URL} type="video/mp4" />
        </video>
      </Box>
      <Box width="100vw" height="100vh" scale="1.1">
        <RiveComponent />
      </Box>
    </>
  );
};
